<template>
  <div>
    <!-- Rename Modal -->
    <v2-modal
      :show="showRenameModal"
      :title="`Rename ${getWishlistTitle()}`"
      @close="showRenameModal = false"
    >
      <v2-input
        :value="wishlistNewName"
        :label="`${getWishlistTitle()} Name`"
        :placeholder="`Enter ${getWishlistTitle()} Name`"
        @input="wishlistNewName = $event"
      />
      <template #footer>
        <v2-button
          variant="outline"
          @click="showRenameModal = false"
        >
          Close
        </v2-button>
        <v2-button @click="onRename">
          Rename
        </v2-button>
      </template>
    </v2-modal>

    <!-- Delete Modal -->
    <v2-modal
      :show="showDeleteModal"
      :title="`Delete ${getWishlistTitle()}`"
      @close="showDeleteModal = false"
    >
      <p class="v2-text-sm">
        This {{ getWishlistTitle() }} will be permanently deleted.
      </p>
      <p class="v2-text-sm">
        Are you sure you want to proceed?
      </p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showDeleteModal = false"
        >
          Close
        </v2-button>
        <v2-button
          variant="destructive"
          @click="onDelete"
        >
          Yes, delete it
        </v2-button>
      </template>
    </v2-modal>

    <!-- Share Modal -->
    <v2-modal
      :show="showShareModal"
      :title="`Share ${getWishlistTitle(isMultipleSelections)}`"
      @close="onCloseShare"
    >
      <div class="v2-mb-4">
        <label class="v2-text-sm v2-font-bold v2-mb-1 v2-block">
          Select Email <span class="v2-text-destructive">*</span>
        </label>
        <v2-dropdown
          :options="allDirectRetailers"
          :value="selectedRetailer"
          placeholder="Select Email"
          label-key="email"
          value-key="retailerId"
          :searchable="true"
          @select="onSelectRetailerAction"
          @search="searchText = $event"
        />
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="onCloseShare"
        >
          Close
        </v2-button>
        <v2-button
          :disabled="isSharing || !email"
          :loading="isSharing"
          @click="onClickShare"
        >
          {{ isSharing ? 'Sharing' : 'Share' }}
        </v2-button>
      </template>
    </v2-modal>

    <!-- Download Modal -->
    <v2-download-wishlist-modal
      :show="showDownloadModal"
      :is-downloading="isDownloading"
      @close="showDownloadModal = false"
      @download="onDownload"
    />

    <!-- Share Link Modal -->
    <v2-share-link-selection-modal
      :show="showShareLinkModal"
      :wishlist-id="wishlist._id"
      :wishlist-name="wishlist.name"
      @close="showShareLinkModal = false"
    />

    <!-- Select Stores Modal (for order form download) -->
    <v2-select-stores-modal
      :show="showSelectStoresModal"
      modal-title="Download Wishlist"
      ok-btn-text="Download"
      :store-list="storesList"
      :is-downloading="isDownloading"
      :show-only-list="true"
      @on-close-modal="showSelectStoresModal = false"
      @on-click-download="onOrderFormDownload"
    />

  </div>
</template>

<script>
import { ref, toRef } from '@vue/composition-api'
import { getWishlistTitle } from '@/@core/utils/utils'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import { useWishlistAction } from '@/composables/useWishlistAction'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'
import UserRoleMixin from '../../apps/UserRoleMixin.vue'
import V2DownloadWishlistModal from './V2DownloadWishlistModal.vue'
import V2ShareLinkSelectionModal from './V2ShareLinkSelectionModal.vue'
import V2SelectStoresModal from '../checkout/V2SelectStoresModal.vue'

export default {
  name: 'V2WishlistAction',
  components: {
    V2Modal,
    V2Button,
    V2Input,
    V2Dropdown,
    V2DownloadWishlistModal,
    V2ShareLinkSelectionModal,
    V2SelectStoresModal,
  },
  mixins: [UserRoleMixin],
  props: {
    wishlist: {
      type: Object,
      default: () => ({}),
    },
    isMultipleSelections: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const wishlistRef = toRef(props, 'wishlist')
    const isMultipleSelectionsRef = toRef(props, 'isMultipleSelections')
    const wa = useWishlistAction(wishlistRef, isMultipleSelectionsRef)

    const showRenameModal = ref(false)
    const showDeleteModal = ref(false)
    const showShareModal = ref(false)
    const showDownloadModal = ref(false)
    const showShareLinkModal = ref(false)
    const showSelectStoresModal = ref(false)

    return {
      ...wa,
      showRenameModal,
      showDeleteModal,
      showShareModal,
      showDownloadModal,
      showShareLinkModal,
      showSelectStoresModal,
      getWishlistTitle,
    }
  },
  mounted() {
    this.initWishlistAction()
  },
  methods: {
    openAction(action) {
      switch (action) {
        case 'rename':
          this.wishlistNewName = this.wishlist.name || ''
          this.showRenameModal = true
          break
        case 'delete':
          this.showDeleteModal = true
          break
        case 'share':
          this.showShareModal = true
          break
        case 'download':
          this.showDownloadModal = true
          break
        case 'share-link':
          this.showShareLinkModal = true
          break
        default:
          break
      }
    },
    async onRename() {
      const result = await this.renameWishlist()
      if (result.success) {
        this.showRenameModal = false
        this.$emit('renamed', this.wishlist)
        this.$emit('fetchWishlists')
      }
    },
    async onDelete() {
      const result = await this.deleteWishlist(this.$router)
      if (result.success) {
        this.showDeleteModal = false
        this.$emit('fetchWishlists')
      }
    },
    onCloseShare() {
      this.showShareModal = false
      this.onCloseShareWishlist()
    },
    onSelectRetailerAction(retailerIdVal) {
      const retailer = this.allDirectRetailers.find(r => r.retailerId === retailerIdVal)
      if (retailer) {
        this.onSelectRetailer(retailer)
      }
    },
    onClickShare() {
      const emitFetchWishlists = () => this.$emit('fetchWishlists')
      const emitOpenReferralModel = (emailVal, wishlistVal) => this.$emit('open-referral-model', emailVal, wishlistVal)
      const emitOnOpenErrorsPopup = messages => this.$emit('on-open-errors-popup', messages)
      const hideModalFn = () => { this.showShareModal = false }
      this.onClickShareWishlist(emitFetchWishlists, emitOpenReferralModel, emitOnOpenErrorsPopup, hideModalFn)
    },
    onDownload(selectedFileFormat) {
      const notifyDownloadsNavMenu = () => { this.$store.commit(SHOW_DOWNLOADS_NAV_INDICATOR) }
      const hideDownloadModalFn = () => { this.showDownloadModal = false }
      const showSelectStoresModalFn = () => { this.showSelectStoresModal = true }
      this.downloadWishlist(selectedFileFormat, notifyDownloadsNavMenu, hideDownloadModalFn, showSelectStoresModalFn)
    },
    onOrderFormDownload(selectedStores) {
      const notifyDownloadsNavMenu = () => { this.$store.commit(SHOW_DOWNLOADS_NAV_INDICATOR) }
      const hideModalFn = () => { this.showSelectStoresModal = false }
      this.downloadOrderForm(selectedStores, notifyDownloadsNavMenu, hideModalFn)
    },
  },
}
</script>

<style lang="scss" scoped>
/* removed - V2Modal body no longer clips */

.v2-text-destructive {
  color: var(--v2-destructive);
}

.v2-block {
  display: block;
}
</style>
